import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { LiteralEInfoModal } from "@/components/shared/LiteralEInfoModal";
import { StyledFileInput } from "@/components/shared/StyledFileInput";

export interface LiteralEStepProps {
  literalE: boolean | null;
  setLiteralE: (v: boolean | null) => void;
  literalEProofFile: File | null;
  setLiteralEProofFile: (v: File | null) => void;
}

export function LiteralEStep({
  literalE,
  setLiteralE,
  literalEProofFile,
  setLiteralEProofFile,
}: LiteralEStepProps): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [literalEInfoOpen, setLiteralEInfoOpen] = useState(false);
  const isAccountant = user?.role === "accountant";

  return (
    <>
      <Card>
        <CardContent className="space-y-4 pt-6">
          {isAccountant ? (
            <p className="text-muted-foreground text-sm">
              {t("onboarding.literalEStepShort")}
            </p>
          ) : (
            <>
              <p className="text-muted-foreground text-sm">
                {t("onboarding.literalEStepExplanation")}
              </p>
              <p className="text-muted-foreground text-sm">
                {t("onboarding.literalEStepBenefits")}{" "}
                <button
                  type="button"
                  onClick={() => setLiteralEInfoOpen(true)}
                  className="text-primary underline transition-colors hover:no-underline focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded"
                >
                  {t("onboarding.literalEInfoModal.whatIsLiteralELink")}
                </button>
              </p>
            </>
          )}
          <div className="space-y-2">
            <Label className="text-foreground">
              {t("onboarding.literalE")}
            </Label>
            {/* String-valued RadioGroup, so the boolean is encoded at the
                boundary; null (unanswered) maps to no value, not to "no".
                Choosing "no" still clears any proof file already picked. */}
            <RadioGroup
              value={literalE === true ? "yes" : literalE === false ? "no" : undefined}
              onValueChange={(v) => {
                if (v === "yes") {
                  setLiteralE(true);
                  return;
                }
                setLiteralE(false);
                setLiteralEProofFile(null);
              }}
              className="flex gap-4 pt-1"
            >
              <Label
                htmlFor="literal-e-no"
                className="flex cursor-pointer items-center gap-2 font-normal"
              >
                <RadioGroupItem id="literal-e-no" value="no" />
                <span>{t("onboarding.literalENo")}</span>
              </Label>
              <Label
                htmlFor="literal-e-yes"
                className="flex cursor-pointer items-center gap-2 font-normal"
              >
                <RadioGroupItem id="literal-e-yes" value="yes" />
                <span>{t("onboarding.literalEYes")}</span>
              </Label>
            </RadioGroup>
          </div>
          {literalE === true && (
            <div className="space-y-3 border-t pt-4">
              <p className="text-muted-foreground text-sm">
                {t("onboarding.literalEProofIntro")}
              </p>
              <p className="text-muted-foreground text-sm font-medium text-foreground">
                {t("onboarding.literalEProofRequired")}
              </p>
              <StyledFileInput
                id="literalEProof"
                accept=".pdf,application/pdf"
                label={t("onboarding.literalEProofLabel")}
                value={literalEProofFile}
                onChange={setLiteralEProofFile}
              />
            </div>
          )}
        </CardContent>
      </Card>
      <LiteralEInfoModal
        open={literalEInfoOpen}
        onOpenChange={setLiteralEInfoOpen}
      />
    </>
  );
}
